import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useChatStore } from "@stores/chat.store";
import { useLocalStorage } from "@hooks/local-storage";
import {
  STORE_NAME,
  STORE_PROPERTIES,
} from "./utils/anti-fraud-message.constants";

import type { TAntiFraudMessageLogicResult } from "./anti-fraud-message.types";

/** Controla visibilidade do aviso anti-fraude (localStorage + estado das mensagens) e timer de auto-close. */
export const useAntiFraudMessageLogic = (
  secondsOpen?: number,
): TAntiFraudMessageLogicResult => {
  const messageListFetched = useChatStore((s) => s.messageList.fetched);
  const messageListSourceLength = useChatStore(
    (s) => s.messageList.source.length,
  );
  const messageListLoading = useChatStore((s) => s.messageList.loading);

  const { storedValue: localStorage, setValue: setLocalStorage } =
    useLocalStorage<Record<string, string>>(STORE_NAME);

  const show = useMemo(() => {
    if (
      messageListFetched &&
      messageListSourceLength &&
      !localStorage?.[STORE_PROPERTIES.hideAntiFraudMessage]
    )
      return true;
    if (messageListLoading) return false;
    return false;
  }, [
    messageListFetched,
    messageListSourceLength,
    messageListLoading,
    localStorage,
  ]);

  const handleCloseAntiFraud = useCallback(() => {
    setLocalStorage("1", STORE_PROPERTIES.hideAntiFraudMessage);
  }, [setLocalStorage]);

  const [timeLeft, setTimeLeft] = useState(secondsOpen ?? 0);
  const closeRef = useRef(handleCloseAntiFraud);

  useEffect(() => {
    closeRef.current = handleCloseAntiFraud;
  }, [handleCloseAntiFraud]);

  useEffect(() => {
    if (timeLeft <= 0) {
      closeRef.current();
      return;
    }

    const timer = setTimeout(() => {
      setTimeLeft((time) => time - 1);
    }, 1000);

    return () => clearTimeout(timer);
  }, [timeLeft]);

  return { show, handleCloseAntiFraud };
};
